import { makeStyles, tokens, Text, Spinner, mergeClasses } from '@coreai/fluentui-react';
import { formatTime } from '@/utils/dateTime';

export type ChatMessageData = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date | string;
  isStreaming?: boolean | undefined;
};

type ChatMessageProps = {
  message: ChatMessageData;
};

const useStyles = makeStyles({
  // Message row with avatar
  messageRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    maxWidth: '85%',
  },
  userMessageRow: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  aiMessageRow: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
  },
  // Compact avatar
  avatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
  },
  userAvatar: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  aiAvatar: {
    backgroundColor: tokens.colorNeutralBackground5,
    color: tokens.colorNeutralForeground1,
  },
  // Message content wrapper (bubble + timestamp)
  messageContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  userMessageContent: {
    alignItems: 'flex-end',
  },
  aiMessageContent: {
    alignItems: 'flex-start',
  },
  // Chat bubble
  bubble: {
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  userBubble: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  aiBubble: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
  },
  timestamp: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  streamingIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginLeft: tokens.spacingHorizontalS,
  },
  emptyContent: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

export const ChatMessage = ({ message }: ChatMessageProps) => {
  const styles = useStyles();
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming && !isUser;

  return (
    <div
      className={mergeClasses(
        styles.messageRow,
        isUser ? styles.userMessageRow : styles.aiMessageRow
      )}
      role="listitem"
      aria-label={`${message.role} message: ${message.content || 'Thinking...'}`}
    >
      {/* Compact avatar */}
      <div
        className={mergeClasses(
          styles.avatar,
          isUser ? styles.userAvatar : styles.aiAvatar
        )}
        aria-hidden="true"
      >
        {isUser ? 'U' : 'A'}
      </div>

      {/* Message content */}
      <div
        className={mergeClasses(
          styles.messageContent,
          isUser ? styles.userMessageContent : styles.aiMessageContent
        )}
      >
        <div
          className={mergeClasses(
            styles.bubble,
            isUser ? styles.userBubble : styles.aiBubble
          )}
        >
          {message.content ? (
            <>
              <Text size={200}>{message.content}</Text>
              {isStreaming && (
                <span className={styles.streamingIndicator}>
                  <Spinner size="extra-tiny" />
                </span>
              )}
            </>
          ) : isStreaming ? (
            <div className={styles.emptyContent}>
              <Spinner size="extra-tiny" />
              <Text size={200}>Thinking...</Text>
            </div>
          ) : (
            <Text size={200}>No content</Text>
          )}
        </div>
        <span className={styles.timestamp}>{formatTime(message.timestamp)}</span>
      </div>
    </div>
  );
};
